import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  delegatedAccountAbi,
  dexAbi,
  erc20AbIsAbi,
  useReadErc20AbIsAllowance,
  useWriteDelegatedAccountCreateAccount,
  useWriteDelegatedAccountSetExchangeApproval,
  useWriteDexCreateAccount,
  useWriteDexDepositCollateral,
  useWriteErc20AbIs,
} from 'sc-interfaces';
import { erc20Abi, formatUnits, getAddress, type Hex } from 'viem';
import { useAccount, useConfig, useReadContract } from 'wagmi';
import { useDialogStore } from '@/components/dialogs/index.ts';
import { useNotify } from '@/feature/notifications';
import { usePositionStore } from '@/feature/terminal_tables/position/PositionStore.ts';
import { sumOpenPositionsEquity } from '@/feature/terminal_tables/position/positionUtils.ts';
import { bigIntToBig, bigToBigInt } from '@/utils/bigUtils.ts';
import { formatTokenAmountDisplay } from '@/utils/tokenNumberUtils.ts';
import { useEnsureChain } from '../../../hooks/useEnsureChain';
import { accountReasonHandler, useAccountErrorHandler } from '../../../hooks/useErrorHandler';
import { useTransactionState } from '../../../hooks/useTransactionMonitor.ts';
import { useWalletNonce } from '../../../hooks/useWalletNonce.ts';
import useAppStore, { useActiveExchangeAddress, useTotalAccountBalance } from '../../../store/rootStore.ts';
import { prepareContractWrite } from '../../../utils/contractWriteHelpers';
import { createRevertDecodingParams, tryDecodeError } from '../../../utils/decodeScError';
import { isUserRejection } from '../../../utils/sentryHelpers';
import { telemetry } from '../../../utils/telemetry';

type DepositTxStep = 'approve' | 'exchangeApproval' | 'main' | null;

export function useDepositProcess({ isDialogOpen = false }: { isDialogOpen?: boolean } = {}) {
  const wagmiConfig = useConfig();
  const notify = useNotify();
  const { address: userAddress } = useAccount();
  const { getNonce } = useWalletNonce();
  const { ensureChain, configChainId } = useEnsureChain();

  const exchangeAddress = useActiveExchangeAddress();
  const configMinDeposit = useAppStore((state) => state.exchange?.minDeposit) ?? 0n;
  const minAccountOpen = useAppStore((state) => state.exchange?.minAccountOpen) ?? 0n;
  const maxEquityN = useAppStore((state) => state.exchange?.maxEquity);
  const token = useAppStore((state) => state.exchange?.token);
  const allPositions = usePositionStore((state) => state.positions);
  const accountBalanceN = useTotalAccountBalance();
  const tokenAddress = token?.address ? getAddress(token.address) : undefined;
  const appStatus = useAppStore((state) => state.status);
  const isDelegated = useAppStore((state) => state.isDelegated);
  const actualExchangeContractAddress = useAppStore((state) => {
    const addr = state.exchange?.contract?.address;
    return addr ? getAddress(addr) : undefined;
  });
  const needsAccountCreation = appStatus !== 'AUTHORIZED';
  const minDeposit = needsAccountCreation ? minAccountOpen : configMinDeposit;

  const balanceAddress = isDelegated ? exchangeAddress : userAddress;
  const { data: walletBalanceValue, refetch: refetchMaxAmount } = useReadContract({
    address: tokenAddress,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: balanceAddress ? [balanceAddress] : undefined,
    chainId: configChainId,
    query: {
      enabled: !!tokenAddress && !!balanceAddress,
      staleTime: 0,
      refetchInterval: isDialogOpen ? 15_000 : false,
    },
  });

  const maxAmount = useMemo(() => {
    if (walletBalanceValue === undefined || !token) return undefined;
    return { value: walletBalanceValue, decimals: token.decimals };
  }, [walletBalanceValue, token]);

  const configMaxDeposit = useMemo(() => {
    // Delegated accounts are enterprise traders with their own risk management — exempt them from the
    // maxEquity deposit cap that applies to retail accounts.
    if (isDelegated) return null;
    if (!maxEquityN || !token) return null;
    try {
      if (Number.isNaN(Number(maxEquityN))) return null;

      // Headroom = maxEquity − current equity (balance + open-position equity).
      const currentEquity = bigIntToBig(accountBalanceN, token.decimals).plus(sumOpenPositionsEquity(allPositions));
      const maxUnusedEquity = bigIntToBig(maxEquityN, token.decimals).minus(currentEquity);
      return maxUnusedEquity.gt(0) ? bigToBigInt(maxUnusedEquity, token.decimals) : 0n;
    } catch {
      return null;
    }
  }, [token, allPositions, accountBalanceN, isDelegated, maxEquityN]);

  const effectiveMaxAmount = useMemo(() => {
    if (!maxAmount) return maxAmount;
    if (configMaxDeposit !== null && configMaxDeposit < maxAmount.value) {
      return { ...maxAmount, value: configMaxDeposit };
    }
    return maxAmount;
  }, [maxAmount, configMaxDeposit]);

  const [operationAmount, setOperationAmount] = useState<bigint>(0n);
  const [txStep, setTxStep] = useState<DepositTxStep>(null);
  const [transactionData, setTransactionData] = useState<Hex | undefined>(undefined);

  const mainCompletedRef = useRef(false);
  const wasAccountCreationRef = useRef(false);

  // Onboarding funnel: a wallet rejection (catch → 'failed') can follow the tx monitor's 'rejected', so the
  // failure is logged once per attempt. The attempted amount lives in a ref because `operationAmount` is
  // only set once the flow reaches a write — a chain-switch rejection fails before that.
  const userRejectedRef = useRef(false);
  const failureLoggedRef = useRef(false);
  const attemptedValueUsdRef = useRef<number | null>(null);

  const { handleError: handleDepositError } = useAccountErrorHandler('deposit');
  const { handleError: handleApprovalError } = useAccountErrorHandler('token approval');

  const {
    writeContractAsync: writeApproveAsync,
    isPending: isApprovePending,
    isError: isApproveError,
    isSuccess: isApproveSubmitted,
    data: approveTxHash,
    reset: resetApprove,
  } = useWriteErc20AbIs({ config: wagmiConfig });

  const {
    writeContractAsync: writeCreateAccountAsync,
    isPending: isCreateAccountPending,
    isError: isCreateAccountError,
    isSuccess: isCreateAccountSubmitted,
    data: createAccountTxHash,
    reset: resetCreateAccount,
  } = useWriteDexCreateAccount({ config: wagmiConfig });

  const {
    writeContractAsync: writeDepositAsync,
    isPending: isDepositPending,
    isError: isDepositError,
    isSuccess: isDepositSubmitted,
    data: depositTxHash,
    reset: resetDeposit,
  } = useWriteDexDepositCollateral({ config: wagmiConfig });

  const {
    writeContractAsync: writeDelegatedCreateAccountAsync,
    isPending: isDelegatedCreateAccountPending,
    isError: isDelegatedCreateAccountError,
    isSuccess: isDelegatedCreateAccountSubmitted,
    data: delegatedCreateAccountTxHash,
    reset: resetDelegatedCreateAccount,
  } = useWriteDelegatedAccountCreateAccount({ config: wagmiConfig });

  const {
    writeContractAsync: writeSetExchangeApprovalAsync,
    isPending: isExchangeApprovalPending,
    isError: isExchangeApprovalError,
    isSuccess: isExchangeApprovalSubmitted,
    data: exchangeApprovalTxHash,
    reset: resetExchangeApproval,
  } = useWriteDelegatedAccountSetExchangeApproval({ config: wagmiConfig });

  // For regular users: check user's allowance to exchange
  // For delegated: check DA's allowance to exchange contract
  const allowanceOwner = isDelegated ? exchangeAddress : userAddress;
  const allowanceSpender = isDelegated ? actualExchangeContractAddress : exchangeAddress;

  const allowanceQuery = useMemo(
    () => ({
      enabled: !!(allowanceOwner && allowanceSpender),
    }),
    [allowanceOwner, allowanceSpender]
  );

  const { data: tokenAllowance, refetch: refetchAllowance } = useReadErc20AbIsAllowance({
    address: tokenAddress,
    args: [allowanceOwner!, allowanceSpender!],
    config: wagmiConfig,
    query: allowanceQuery,
    chainId: configChainId,
  });

  const requestApprovedAmount = useCallback(() => {
    // refetch() bypasses TanStack Query's `enabled` flag, so guard manually —
    // otherwise we hit InvalidAddressError on undefined args / "address is required" on useBalance.
    if (allowanceOwner && allowanceSpender) {
      refetchAllowance?.();
    }
    if (balanceAddress) {
      refetchMaxAmount();
    }
  }, [refetchAllowance, refetchMaxAmount, allowanceOwner, allowanceSpender, balanceAddress]);

  const resetProcess = useCallback(() => {
    setCurrentStatus('idle');
    setOperationAmount(0n);
    setTxStep(null);
    setTransactionData(undefined);
    resetApprove();
    resetCreateAccount();
    resetDeposit();
    resetExchangeApproval();
    resetDelegatedCreateAccount();
  }, [resetApprove, resetCreateAccount, resetDeposit, resetExchangeApproval, resetDelegatedCreateAccount]);

  useEffect(() => {
    if (exchangeAddress) {
      resetProcess();
    }
  }, [exchangeAddress, resetProcess]);

  // Refetch on-chain wallet balance + allowance whenever the DEX account balance changes — deposits,
  // withdrawals, liquidations, funding, and PnL settlements all imply prior cached values may be stale.
  const prevAccountBalanceRef = useRef(accountBalanceN);
  useEffect(() => {
    if (prevAccountBalanceRef.current === accountBalanceN) return;
    prevAccountBalanceRef.current = accountBalanceN;
    requestApprovedAmount();
  }, [accountBalanceN, requestApprovedAmount]);

  const startExchangeApproval = useCallback(
    async (amount: bigint) => {
      if (!exchangeAddress || !userAddress) return;

      setOperationAmount(amount);
      setTxStep('exchangeApproval');
      setCurrentStatus('preparing');

      const prepared = await prepareContractWrite({
        config: wagmiConfig,
        address: exchangeAddress,
        abi: delegatedAccountAbi,
        functionName: 'setExchangeApproval',
        args: [amount] as const,
        account: userAddress,
        chainId: configChainId,
      });

      setCurrentStatus('submitting');
      setTransactionData(prepared.transactionData);
      const nonce = await getNonce();
      await writeSetExchangeApprovalAsync({ ...(prepared.writeArgs as any), nonce } as any);
    },
    [exchangeAddress, userAddress, wagmiConfig, writeSetExchangeApprovalAsync, getNonce, configChainId]
  );

  const startMainAction = useCallback(
    async (operationAmount: bigint) => {
      if (!exchangeAddress || !userAddress || operationAmount <= 0n) {
        return;
      }

      mainCompletedRef.current = false;
      wasAccountCreationRef.current = needsAccountCreation;

      setOperationAmount(operationAmount);
      setCurrentStatus('preparing');
      setTxStep('main');
      // Owns its failure handling because the approve → main hand-off in `onSuccess` calls this
      // un-awaited: without a catch here, rejecting the second wallet prompt leaves the rejection
      // unhandled, `userRejectedRef` false, and the funnel reports `tx_error` instead of
      // `user_rejected` — the path every first-time depositor takes. A throw from
      // `prepareContractWrite` would otherwise leave the status on 'preparing' forever.
      try {
        const useDelegatedCreateAccount = isDelegated && needsAccountCreation;
        const functionName = needsAccountCreation ? 'createAccount' : 'depositCollateral';
        const abi = useDelegatedCreateAccount ? delegatedAccountAbi : dexAbi;
        const prepared = await prepareContractWrite({
          config: wagmiConfig,
          address: exchangeAddress,
          abi,
          functionName,
          args: [operationAmount] as const,
          account: userAddress,
          chainId: configChainId,
        });

        setCurrentStatus('submitting');
        setTransactionData(prepared.transactionData);

        const nonce = await getNonce();

        if (needsAccountCreation) {
          if (isDelegated) {
            await writeDelegatedCreateAccountAsync({ ...(prepared.writeArgs as any), nonce } as any);
          } else {
            await writeCreateAccountAsync({ ...(prepared.writeArgs as any), nonce } as any);
          }
        } else {
          await writeDepositAsync({ ...(prepared.writeArgs as any), nonce } as any);
        }
      } catch (error) {
        handleDepositError(error);
        userRejectedRef.current = isUserRejection(error);
        setCurrentStatus('failed');
      }
    },
    [
      exchangeAddress,
      userAddress,
      wagmiConfig,
      needsAccountCreation,
      isDelegated,
      writeCreateAccountAsync,
      writeDelegatedCreateAccountAsync,
      writeDepositAsync,
      handleDepositError,
      getNonce,
      ensureChain,
      configChainId,
    ]
  );

  const completeMainStep = useCallback(() => {
    if (mainCompletedRef.current) return;
    mainCompletedRef.current = true;

    requestApprovedAmount();
    if (wasAccountCreationRef.current) {
      if (isDelegated) {
        resetDelegatedCreateAccount();
      } else {
        resetCreateAccount();
      }
      useDialogStore.getState().openDialog('oneClickTradingDialog');
    } else {
      resetDeposit();
    }
    const valueUsd = Number(formatUnits(operationAmount, token!.decimals));
    telemetry.logDeposit(token?.symbol || 'Unknown', valueUsd);
    telemetry.logOnboarding('deposit_confirmed', { value_usd: valueUsd, is_account_creation: wasAccountCreationRef.current });
    notify(
      `Deposit received. ${formatTokenAmountDisplay(operationAmount, token!)} ${token?.symbol || ''} added to your account.`,
      'success'
    );
    setTxStep(null);
  }, [
    requestApprovedAmount,
    isDelegated,
    resetCreateAccount,
    resetDelegatedCreateAccount,
    resetDeposit,
    token,
    operationAmount,
    notify,
  ]);

  const messages = useMemo(() => {
    if (txStep === 'approve') {
      return {
        confirming: 'Approving token for deposit. This may take a moment...',
        failed: 'Token approval not completed. This is required to proceed with your deposit.',
        rejected: 'Token approval not completed. This is required to proceed with your deposit.',
        error: 'Approval status could not be determined. Please check your wallet for details.',
      };
    } else if (txStep === 'exchangeApproval') {
      return {
        confirming: 'Setting exchange approval on delegated account. This may take a moment...',
        failed: 'Exchange approval not completed. This is required to proceed with your deposit.',
        rejected: 'Exchange approval not completed. This is required to proceed with your deposit.',
        error: 'Approval status could not be determined. Please check your wallet for details.',
      };
    } else {
      return {
        confirming: `Processing your deposit. This may take a moment...`,
        failed: `Deposit failed. The transaction was reverted by the contract.`,
        rejected: `Transaction not completed. You can try again when ready.`,
        error: 'Transaction status could not be determined. Please check your wallet for details.',
      };
    }
  }, [txStep, operationAmount, token]);

  let activeTransactionState: any;
  switch (txStep) {
    case 'approve':
      activeTransactionState = {
        isPending: isApprovePending,
        isSuccess: isApproveSubmitted,
        isError: isApproveError,
        txHash: approveTxHash,
      };
      break;
    case 'exchangeApproval':
      activeTransactionState = {
        isPending: isExchangeApprovalPending,
        isSuccess: isExchangeApprovalSubmitted,
        isError: isExchangeApprovalError,
        txHash: exchangeApprovalTxHash,
      };
      break;
    case 'main':
      if (needsAccountCreation && isDelegated) {
        activeTransactionState = {
          isPending: isDelegatedCreateAccountPending,
          isSuccess: isDelegatedCreateAccountSubmitted,
          isError: isDelegatedCreateAccountError,
          txHash: delegatedCreateAccountTxHash,
        };
      } else if (needsAccountCreation) {
        activeTransactionState = {
          isPending: isCreateAccountPending,
          isSuccess: isCreateAccountSubmitted,
          isError: isCreateAccountError,
          txHash: createAccountTxHash,
        };
      } else {
        activeTransactionState = {
          isPending: isDepositPending,
          isSuccess: isDepositSubmitted,
          isError: isDepositError,
          txHash: depositTxHash,
        };
      }
      break;
    default:
      activeTransactionState = { isPending: false, isSuccess: false, isError: false, txHash: undefined };
  }

  const { currentStatus, setCurrentStatus, isPending } = useTransactionState({
    isWritePending: activeTransactionState.isPending,
    isWriteSuccess: activeTransactionState.isSuccess,
    isWriteError: activeTransactionState.isError,
    txHash: activeTransactionState.txHash,
    processData: operationAmount,
    messages,
    onSuccess: useCallback(() => {
      if (txStep === 'approve') {
        resetApprove();
        startMainAction(operationAmount);
      } else if (txStep === 'exchangeApproval') {
        resetExchangeApproval();
        startMainAction(operationAmount);
      } else if (txStep === 'main') {
        completeMainStep();
      }
    }, [txStep, operationAmount, resetApprove, resetExchangeApproval, startMainAction, completeMainStep]),
    revertDecoding: txStep === 'main' ? createRevertDecodingParams(exchangeAddress, transactionData, userAddress) : undefined,
    reasonHandler: accountReasonHandler,
  });

  // Single drop point for every failure path: wallet rejection (catch → failed), write error (→ rejected),
  // revert or receipt timeout (→ failed).
  useEffect(() => {
    if (currentStatus !== 'failed' && currentStatus !== 'rejected') return;
    if (failureLoggedRef.current) return;
    failureLoggedRef.current = true;
    telemetry.logOnboarding('failed', {
      step: 'deposit',
      reason: userRejectedRef.current ? 'user_rejected' : 'tx_error',
      stage: txStep,
      outcome: currentStatus,
      ...(attemptedValueUsdRef.current != null ? { value_usd: attemptedValueUsdRef.current } : {}),
    });
  }, [currentStatus, txStep]);

  // Fallback for account creation: if the backend reports the account as AUTHORIZED while the main-step tx
  // is still in flight, treat that as success. Status is unambiguous — unlike balance, which can move for
  // unrelated reasons (liquidation, realized PnL, funding).
  useEffect(() => {
    if (txStep !== 'main' || !wasAccountCreationRef.current) return;
    if (appStatus !== 'AUTHORIZED') return;

    setCurrentStatus('confirmed');
    completeMainStep();
  }, [appStatus, txStep, completeMainStep, setCurrentStatus]);

  const startDepositCollateral = useCallback(
    async ({ approveAmount }: { approveAmount: bigint }) => {
      if (isPending) return;
      if (!exchangeAddress || !tokenAddress || !userAddress) {
        notify('Deposit configuration is missing. Please try again later.', 'error');
        return;
      }

      userRejectedRef.current = false;
      failureLoggedRef.current = false;
      attemptedValueUsdRef.current = Number(formatUnits(approveAmount, token!.decimals));
      telemetry.logOnboarding('deposit_started', {
        value_usd: attemptedValueUsdRef.current,
        is_account_creation: needsAccountCreation,
      });

      try {
        setCurrentStatus('switching_chain');
        await ensureChain();
        // Read the live allowance before deciding. A stale/undefined cache would make us issue a redundant
        // approval for `approveAmount`, overwriting an existing (possibly unlimited) approval
        let currentAllowance = tokenAllowance;
        if (allowanceOwner && allowanceSpender) {
          const result = await refetchAllowance?.();
          if (result?.data !== undefined) currentAllowance = result.data;
        }
        const needsApproval = (currentAllowance ?? 0n) < approveAmount;

        if (isDelegated) {
          // Don't pre-judge owner vs operator — attempt the on-chain action and let the contract decide.
          if (needsApproval) {
            try {
              await startExchangeApproval(approveAmount);
            } catch (error) {
              // setExchangeApproval is owner-only; an operator reaching here means approval is missing and
              // they can't grant it. Give that specific guidance; fall back to the decoded revert otherwise.
              if (tryDecodeError(error)?.errorName === 'OwnableUnauthorizedAccount') {
                notify('The delegated account needs exchange approval, which only the account owner can grant.', 'error');
              } else {
                handleApprovalError(error);
              }
              userRejectedRef.current = isUserRejection(error);
              setCurrentStatus('failed');
              return;
            }
          } else {
            await startMainAction(approveAmount);
          }
        } else {
          if (needsApproval) {
            setOperationAmount(approveAmount);
            setCurrentStatus('preparing');
            setTxStep('approve');
            const prepared = await prepareContractWrite({
              config: wagmiConfig,
              address: tokenAddress,
              abi: erc20AbIsAbi,
              functionName: 'approve',
              args: [exchangeAddress, approveAmount] as const,
              account: userAddress,
              chainId: configChainId,
            });
            setCurrentStatus('submitting');
            const nonce = await getNonce();
            await writeApproveAsync({ ...(prepared.writeArgs as any), nonce } as any);
          } else {
            await startMainAction(approveAmount);
          }
        }
      } catch (error) {
        if (txStep === 'approve' || txStep === 'exchangeApproval') {
          handleApprovalError(error);
        } else {
          handleDepositError(error);
        }
        userRejectedRef.current = isUserRejection(error);
        setCurrentStatus('failed');
      }
    },
    [
      isPending,
      token,
      needsAccountCreation,
      tokenAllowance,
      refetchAllowance,
      allowanceOwner,
      allowanceSpender,
      exchangeAddress,
      tokenAddress,
      userAddress,
      wagmiConfig,
      writeApproveAsync,
      startMainAction,
      startExchangeApproval,
      isDelegated,
      setCurrentStatus,
      handleApprovalError,
      handleDepositError,
      notify,
      getNonce,
      ensureChain,
      configChainId,
    ]
  );

  return {
    token,
    minDeposit,
    maxAmount: effectiveMaxAmount,
    maxEquity: isDelegated ? undefined : (maxEquityN ?? undefined),
    startDepositCollateral,
    resetProcess,
    currentStatus,
    isPending,
    isSuccess: currentStatus === 'confirmed' && !txStep,
    requestApprovedAmount,
    isMaxEquityLimited: !!maxAmount && configMaxDeposit != null && configMaxDeposit < maxAmount.value,
  };
}
